import { useMemo, useState } from 'react';
import { SmilePlusIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { useConversation } from '../../context';
import type { TextOrFileMessage } from '../../types';
import { getEmojiImageUrl, groupReactions } from '../../utils';
import { ReactionPicker } from './ReactionPicker';

/**
 * Displays grouped emoji reactions below a message bubble.
 * @see {@link ChatMessageBubble}
 */
export function ChatMessageReactions({
    className,
    message,
    isOutgoing,
    onReact,
}: {
    className?: string;
    message: TextOrFileMessage;
    isOutgoing: boolean;
    onReact?: (message: TextOrFileMessage, reaction: string) => void;
}) {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const { selfParticipant } = useConversation();
    const groupedReactions = useMemo(() => {
        return groupReactions(message.Reactions, selfParticipant);
    }, [message.Reactions, selfParticipant]);

    return (
        <div
            className={cn(
                'flex flex-wrap content-start items-center gap-[4px]',
                isOutgoing ? 'justify-end' : 'justify-start',
                className,
            )}
        >
            {groupedReactions.map((reaction) => (
                <TooltipProvider key={reaction.emoji} delayDuration={500}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className={cn(
                                    'flex h-[24px] items-center justify-center gap-[5px] rounded-full border px-[6px] transition-colors',
                                    reaction.hasReacted
                                        ? 'border-brick-900 text-brick-900 hover:bg-brick-100/10'
                                        : 'border-gray-400 text-gray-700 hover:bg-white/20',
                                )}
                                onClick={() =>
                                    onReact?.(message, reaction.emoji)
                                }
                            >
                                <Emoji emoji={reaction.emoji} />
                                <span className="text-[11px] font-semibold tabular-nums leading-none">
                                    {reaction.count}
                                </span>
                            </button>
                        </TooltipTrigger>
                        <TooltipContent
                            className="max-w-[200px] break-words"
                            side="top"
                        >
                            {reaction.participantNames.join(', ')}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ))}
            <ReactionPicker
                open={isPickerOpen}
                onOpenChange={setIsPickerOpen}
                onReact={(reaction) => onReact?.(message, reaction)}
            >
                <button
                    type="button"
                    className="grid h-[24px] w-[28px] place-items-center rounded-full border border-gray-300 bg-white/40 text-gray-600 transition-colors hover:border-gray-400 hover:bg-white/30 hover:text-gray-900"
                    aria-label="Add reaction"
                >
                    <SmilePlusIcon className="size-[14px]" />
                </button>
            </ReactionPicker>
        </div>
    );
}

/**
 * The component that displays the emoji reaction. Renders an image and fallbacks
 * to the Unicode emoji if the image is not available.
 */
function Emoji({ emoji }: { emoji: string }) {
    const url = getEmojiImageUrl(emoji);

    return url ? (
        <img className="size-[16px]" src={url} alt={emoji} />
    ) : (
        <span className="text-[16px] leading-none">{emoji}</span>
    );
}
